import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut, Menu, X, Globe, Search, ChevronRight, ChevronLeft
} from "lucide-react";
import { Avatar, ThemeSwitcher } from "../ui";
import { T } from "../../constants";
import AnnouncementBell from "../features/announcements/AnnouncementBell";

export default function AppShell({
  nav,
  setNav,
  navItems,
  user,
  lang,
  setLang,
  onLogout,
  children,
}) {
  const t = T[lang];
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Memoize derived values
  const currentNavItem = useMemo(() => navItems.find((n) => n.id === nav), [navItems, nav]);
  const schoolInitial = useMemo(() => 
    user.school?.name ? user.school.name.charAt(0).toUpperCase() : "EP"
  , [user.school?.name]);
  
  const todayDate = useMemo(() => 
    new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
  , []);

  // Handlers
  const toggleSidebar = useCallback(() => setSidebarOpen(p => !p), []);
  const toggleLang = useCallback(() => setLang(lang === "en" ? "hi" : "en"), [lang, setLang]);

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {sidebarOpen && (
          <motion.aside
            className="w-[260px] bg-slate-900 flex flex-col shrink-0 relative"
            initial={{ x: -260, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -260, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Logo area */}
            <div className="px-5 pt-6 pb-5 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-indigo-500/25 shrink-0">
                  {schoolInitial}
                </div>
                <div className="overflow-hidden">
                  <div className="font-semibold text-base text-white leading-tight truncate">
                    {user.school?.name || (
                      <>
                        eduPulse<span className="text-indigo-400">AI</span>
                      </>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">
                    {user.role}
                  </div>
                </div>
              </div>
            </div>

            {/* Nav links */}
            <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-1">
              {navItems.map((n, i) => {
                const Icon = n.ico;
                const isActive = nav === n.id;

                return (
                  <motion.button
                    key={n.id}
                    onClick={() => setNav(n.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                      isActive 
                        ? "bg-indigo-500/10 text-indigo-400" 
                        : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                    }`}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.2 }}
                    whileHover={{ x: 2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                      isActive ? "bg-indigo-500/20" : "bg-slate-800 group-hover:bg-slate-700"
                    }`}>
                      <Icon size={17} strokeWidth={isActive ? 2.5 : 2} />
                    </span>
                    <span className="flex-1 text-left">{n.lbl}</span>
                    {isActive && (
                      <ChevronRight size={14} className="text-indigo-400 opacity-60" />
                    )}
                  </motion.button>
                );
              })}
            </nav>

            {/* User strip */}
            <div className="px-4 py-4 border-t border-slate-800">
              <div className="flex gap-3 items-center mb-3">
                <Avatar
                  t={(user.name || user.id).slice(0, 2).toUpperCase()}
                  size={38}
                  bg="#334155"
                  className="rounded-xl"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-200 truncate">
                    {user.name || user.id}
                  </div>
                  <div className="text-xs text-slate-500 font-mono truncate">
                    {user.id}
                  </div>
                </div>
              </div>
              <button
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                           bg-slate-800 text-slate-300 text-sm font-medium
                           transition-all duration-200 hover:bg-rose-500/10 hover:text-rose-400"
              >
                <LogOut size={15} />
                {t.logout}
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* ── Main area ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-5 lg:px-6 shrink-0">
          {/* Left: Toggle + Page title */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-500
                         transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700"
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            >
              {sidebarOpen ? <ChevronLeft size={18} /> : <Menu size={18} />}
            </button>

            <div className="h-6 w-px bg-slate-200 mx-1" />

            <div className="flex items-center gap-2">
              {currentNavItem?.ico && (
                <currentNavItem.ico size={16} className="text-slate-400" />
              )}
              <h1 className="text-lg font-semibold text-slate-800">
                {currentNavItem?.lbl || ""}
              </h1>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative hidden sm:block">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-48 lg:w-56 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50
                           text-sm text-slate-700 placeholder:text-slate-400
                           focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20
                           transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Announcements bell */}
            <AnnouncementBell user={user} />

            {/* Date */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 text-sm text-slate-500">
              <span className="font-mono">{todayDate}</span>
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-slate-200 mx-1" />

            {/* Theme Switcher */}
            <ThemeSwitcher variant="button" />

            {/* Language toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 text-sm font-medium
                         text-slate-600 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-800"
            >
              <Globe size={14} />
              <span className="uppercase text-xs tracking-wide">{lang}</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-slate-50/50">
          <AnimatePresence mode="wait">
            <motion.div
              key={nav}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="px-5 lg:px-8 py-6 min-h-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
